import { io, type Socket } from 'socket.io-client';
import type {
  AvailabilityState,
  ConversationMode,
  MessageDto,
  ShowPopupEvent,
} from '@intent/shared';
import type { WidgetConfig } from './config';
import type { Transport } from './transport';

export interface ChatState {
  messages: MessageDto[];
  mode: ConversationMode;
  isAgentTyping: boolean;
  connected: boolean;
  projectName: string;
  greeting: string;
  /** Founder availability at bootstrap, driving the presence label. */
  availabilityState: AvailabilityState;
  /** An email-capture popup a rule asked to show, or null. */
  activePopup: ShowPopupEvent | null;
}

interface ChatClientOptions {
  config: WidgetConfig;
  transport: Transport;
  visitorId: string;
  sessionId: string;
  projectName: string;
  greeting: string;
  socketUrl: string;
  availabilityState: AvailabilityState;
  initialConversation: { id: string; mode: ConversationMode; messages: MessageDto[] } | null;
}

/**
 * Conversation state and its socket connection.
 *
 * Deliberately free of React so the transport logic can be reasoned about and
 * replaced independently of the view. The UI subscribes; this owns the truth.
 */
export class ChatClient {
  private state: ChatState;
  private listeners = new Set<(state: ChatState) => void>();
  private socket: Socket | null = null;
  private conversationId: string | null;
  private typingTimer: number | null = null;
  private lastTypingSent = false;

  constructor(private readonly options: ChatClientOptions) {
    this.conversationId = options.initialConversation?.id ?? null;

    this.state = {
      messages: options.initialConversation?.messages ?? [],
      mode: options.initialConversation?.mode ?? 'AI',
      isAgentTyping: false,
      connected: false,
      projectName: options.projectName,
      greeting: options.greeting,
      availabilityState: options.availabilityState,
      activePopup: null,
    };
  }

  getState = (): ChatState => this.state;

  subscribe(listener: (state: ChatState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<ChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  /** Opens the realtime channel. Chat still works over HTTP without it. */
  connect(): void {
    this.socket = io(`${this.options.socketUrl}/widget`, {
      transports: ['websocket', 'polling'],
      withCredentials: false,
      auth: {
        projectKey: this.options.config.projectKey,
        visitorId: this.options.visitorId,
        sessionId: this.options.sessionId,
      },
    });

    this.socket.on('connect', () => this.setState({ connected: true }));
    this.socket.on('disconnect', () => this.setState({ connected: false }));

    this.socket.on('chat_message', (payload: { conversationId: string; message: MessageDto }) => {
      this.conversationId ??= payload.conversationId;
      this.appendMessage(payload.message);
    });

    this.socket.on('typing', (payload: { actor: string; isTyping: boolean }) => {
      // The visitor's own typing is never echoed back to them.
      if (payload.actor === 'visitor') return;
      this.setState({ isAgentTyping: payload.isTyping });
    });

    this.socket.on('takeover_started', () => this.setState({ mode: 'HUMAN', isAgentTyping: false }));
    this.socket.on('takeover_ended', () => this.setState({ mode: 'AI' }));

    // First popup to arrive wins until dismissed — never stack modals.
    this.socket.on('show_popup', (payload: ShowPopupEvent) => {
      if (!this.state.activePopup) this.setState({ activePopup: payload });
    });
  }

  /**
   * Appends a message, ignoring duplicates.
   *
   * A message the visitor sends arrives twice — once as the POST response and
   * once over the socket — and rendering both would show every message twice.
   */
  private appendMessage(message: MessageDto): void {
    if (this.state.messages.some((existing) => existing.id === message.id)) return;

    this.setState({
      messages: [...this.state.messages, message],
      ...(message.role !== 'VISITOR' ? { isAgentTyping: false } : {}),
    });
  }

  async sendMessage(content: string): Promise<void> {
    this.setTyping(false);

    const response = await this.options.transport.sendChatMessage({
      visitorId: this.options.visitorId,
      sessionId: this.options.sessionId,
      conversationId: this.conversationId,
      content,
    });

    this.conversationId = response.conversationId;
    this.appendMessage(response.message);
  }

  /** Submits the active popup's email; returns whether it was newly captured. */
  async submitPopupEmail(email: string): Promise<boolean> {
    const popup = this.state.activePopup;
    if (!popup) return false;

    const result = await this.options.transport.submitPopupEmail({
      visitorId: this.options.visitorId,
      sessionId: this.options.sessionId,
      ruleId: popup.ruleId,
      email,
    });
    return result.captured;
  }

  dismissPopup(): void {
    this.setState({ activePopup: null });
  }

  /**
   * Reports typing state, debounced.
   *
   * Emitting on every keystroke would be one socket frame per character; the
   * receiving end only needs to know that typing is happening.
   */
  setTyping(isTyping: boolean): void {
    if (!this.socket) return;

    if (this.typingTimer !== null) {
      window.clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }

    if (isTyping) {
      if (!this.lastTypingSent) {
        this.socket.emit('typing', { isTyping: true });
        this.lastTypingSent = true;
      }

      // Auto-clear, so an abandoned draft does not leave the founder watching
      // a typing indicator forever.
      this.typingTimer = window.setTimeout(() => this.setTyping(false), 3_000);
      return;
    }

    if (this.lastTypingSent) {
      this.socket.emit('typing', { isTyping: false });
      this.lastTypingSent = false;
    }
  }

  destroy(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.listeners.clear();
  }
}
